import type { DiagramEdge } from '../types/schema'
import type { NodePosition } from './edge-offset'

// Handle faces used for distributing source/target handles
const HANDLE_FACES = ['top', 'bottom', 'left', 'right'] as const

type HandleFace = typeof HANDLE_FACES[number]

type Role = 'source' | 'target'

type HandlePreference = 'auto' | 'vertical' | 'horizontal'

const ORIENTATION_VERTICAL = new Set<HandleFace>(['top', 'bottom'])
const ORIENTATION_HORIZONTAL = new Set<HandleFace>(['left', 'right'])

const TARGET_HANDLE_OPTIONS: Record<HandleFace, string[]> = {
  top: ['top-left-target', 'top-center-target', 'top-right-target'],
  bottom: ['bottom-left-target', 'bottom-center-target', 'bottom-right-target'],
  left: ['left-top-target', 'left-center-target', 'left-bottom-target'],
  right: ['right-top-target', 'right-center-target', 'right-bottom-target'],
}

const SOURCE_HANDLE_OPTIONS: Record<HandleFace, string[]> = {
  top: ['top-left-source', 'top-center-source', 'top-right-source'],
  bottom: ['bottom-left-source', 'bottom-center-source', 'bottom-right-source'],
  left: ['left-top-source', 'left-center-source', 'left-bottom-source'],
  right: ['right-top-source', 'right-center-source', 'right-bottom-source'],
}

const BALANCED_PATTERN_THREE = [0, 2, 1]
const BALANCED_PATTERN_TWO = [0, 1]

function isHandleFace(value: unknown): value is HandleFace {
  return typeof value === 'string' && (HANDLE_FACES as readonly string[]).includes(value)
}

function getOrientationOverride(edge: DiagramEdge, role: Role): HandleFace | undefined {
  const orientationValue = role === 'target'
    ? edge.data?.targetOrientation
    : edge.data?.sourceOrientation

  if (orientationValue === 'auto' || !isHandleFace(orientationValue)) {
    return undefined
  }

  return orientationValue
}

function getHandleOptions(face: HandleFace, role: Role): string[] {
  return role === 'target' ? TARGET_HANDLE_OPTIONS[face] : SOURCE_HANDLE_OPTIONS[face]
}

function pickHandleIndex(order: number, optionCount: number): number {
  if (optionCount <= 1) {
    return 0
  }

  if (optionCount === 2) {
    return BALANCED_PATTERN_TWO[order % BALANCED_PATTERN_TWO.length]
  }

  if (optionCount === 3) {
    return BALANCED_PATTERN_THREE[order % BALANCED_PATTERN_THREE.length]
  }

  return order % optionCount
}

function determineTargetFace(
  edge: DiagramEdge,
  nodePositions: Map<string, NodePosition>
): HandleFace | undefined {
  const explicit = getOrientationOverride(edge, 'target')
  if (explicit) {
    return explicit
  }

  const sourcePos = nodePositions.get(edge.source)
  const targetPos = nodePositions.get(edge.target)

  if (!sourcePos || !targetPos) {
    return undefined
  }

  const dx = sourcePos.x - targetPos.x
  const dy = sourcePos.y - targetPos.y

  return pickFaceForVector({ dx, dy }, edge)
}

function determineSourceFace(
  edge: DiagramEdge,
  nodePositions: Map<string, NodePosition>
): HandleFace | undefined {
  const explicit = getOrientationOverride(edge, 'source')
  if (explicit) {
    return explicit
  }

  const sourcePos = nodePositions.get(edge.source)
  const targetPos = nodePositions.get(edge.target)

  if (!sourcePos || !targetPos) {
    return undefined
  }

  const dx = targetPos.x - sourcePos.x
  const dy = targetPos.y - sourcePos.y

  return pickFaceForVector({ dx, dy }, edge)
}

function pickFaceForVector(
  vector: { dx: number; dy: number },
  edge: DiagramEdge
): HandleFace | undefined {
  const preference = getAxisPreference(edge)

  const selectVertical = () => {
    if (vector.dy < 0) return 'top'
    if (vector.dy > 0) return 'bottom'
    return undefined
  }

  const selectHorizontal = () => {
    if (vector.dx < 0) return 'left'
    if (vector.dx > 0) return 'right'
    return undefined
  }

  if (preference === 'vertical') {
    return selectVertical() ?? selectHorizontal()
  }

  if (preference === 'horizontal') {
    return selectHorizontal() ?? selectVertical()
  }

  const preferVertical = Math.abs(vector.dy) >= Math.abs(vector.dx)
  if (preferVertical) {
    return selectVertical() ?? selectHorizontal()
  }
  return selectHorizontal() ?? selectVertical()
}

function getAxisPreference(edge: DiagramEdge): HandlePreference {
  const orientations: HandleFace[] = []
  const sourceOrientation = edge.data?.sourceOrientation
  if (isHandleFace(sourceOrientation)) {
    orientations.push(sourceOrientation)
  }
  const targetOrientation = edge.data?.targetOrientation
  if (isHandleFace(targetOrientation)) {
    orientations.push(targetOrientation)
  }

  const hasVertical = orientations.some((face) => ORIENTATION_VERTICAL.has(face))
  const hasHorizontal = orientations.some((face) => ORIENTATION_HORIZONTAL.has(face))

  if (hasVertical && !hasHorizontal) {
    return 'vertical'
  }

  if (hasHorizontal && !hasVertical) {
    return 'horizontal'
  }

  const preference = edge.data?.handlePreference
  if (preference === 'vertical' || preference === 'horizontal') {
    return preference
  }

  return 'auto'
}

function assignHandlesForRole(
  edges: DiagramEdge[],
  nodePositions: Map<string, NodePosition>,
  role: Role
): void {
  const groups = new Map<string, DiagramEdge[]>()

  edges.forEach((edge) => {
    const existingHandle = role === 'target' ? edge.targetHandle : edge.sourceHandle
    if (existingHandle) {
      return
    }

    const key = role === 'target' ? edge.target : edge.source
    if (!key) {
      return
    }

    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key)!.push(edge)
  })

  groups.forEach((groupEdges) => {
    const faceBuckets = new Map<HandleFace, DiagramEdge[]>()

    groupEdges.forEach((edge) => {
      const face = role === 'target'
        ? determineTargetFace(edge, nodePositions)
        : determineSourceFace(edge, nodePositions)

      if (!face) {
        return
      }

      if (!faceBuckets.has(face)) {
        faceBuckets.set(face, [])
      }
      faceBuckets.get(face)!.push(edge)
    })

    faceBuckets.forEach((edgesForFace, face) => {
      const options = getHandleOptions(face, role)
      if (options.length === 0) {
        return
      }

      edgesForFace.forEach((edge, index) => {
        const optionIndex = pickHandleIndex(index, options.length)
        const handleId = options[optionIndex]

        if (role === 'target') {
          edge.targetHandle = handleId
        } else {
          edge.sourceHandle = handleId
        }
      })
    })
  })
}

export function autoAssignHandles(
  edges: DiagramEdge[],
  nodePositions: NodePosition[]
): DiagramEdge[] {
  if (!edges.length || !nodePositions.length) {
    return edges
  }

  // Track which edges arrived with explicit handles so the port-separation
  // pass below never touches user-/AI-provided connection points.
  const hadExplicitSource = edges.map((edge) => Boolean(edge.sourceHandle))
  const hadExplicitTarget = edges.map((edge) => Boolean(edge.targetHandle))

  // Work on a shallow copy so original edge objects stay untouched
  const result = edges.map((edge) => ({ ...edge }))
  const nodePositionMap = new Map(nodePositions.map((node) => [node.id, node]))

  assignHandlesForRole(result, nodePositionMap, 'target')
  assignHandlesForRole(result, nodePositionMap, 'source')

  separatePortsForNodePairs(result, nodePositionMap, hadExplicitSource, hadExplicitTarget)

  return result
}

/**
 * Port-based separation for parallel and bidirectional edges.
 *
 * The principled fix for overlapping parallel (multiple A→B) and bidirectional
 * (A→B and B→A) edges is to give each one DISTINCT perimeter ports rather than
 * relying solely on post-hoc curve offsets. This pass detects groups of edges
 * sharing the same unordered node pair and:
 *
 *  - Parallel group on the same face: spreads the edges across the 3 named
 *    handles for that face (left/center/right) so they leave/enter at distinct
 *    points. Edges that receive distinct ports are flagged `portSeparated` so the
 *    offset pass can drop their curve offset to 0 (ports already separate them).
 *  - Bidirectional pair: keeps the two directions on opposite handles so they do
 *    not overlap, and flags them `portSeparated`.
 *
 * Solitary edges and edges that arrived with explicit handles are left untouched,
 * preserving existing single-edge behavior.
 */
function separatePortsForNodePairs(
  edges: DiagramEdge[],
  nodePositions: Map<string, NodePosition>,
  hadExplicitSource: boolean[],
  hadExplicitTarget: boolean[]
): void {
  // Group edge indices by unordered node pair (A<->B).
  const pairGroups = new Map<string, number[]>()

  edges.forEach((edge, index) => {
    if (!edge.source || !edge.target || edge.source === edge.target) {
      return
    }
    const key = pairKey(edge.source, edge.target)
    if (!pairGroups.has(key)) {
      pairGroups.set(key, [])
    }
    pairGroups.get(key)!.push(index)
  })

  pairGroups.forEach((indices) => {
    if (indices.length < 2) {
      // Solitary connection between the two nodes: nothing to separate.
      return
    }

    // Split the group by direction so we can detect bidirectional pairs.
    const byDirection = new Map<string, number[]>()
    indices.forEach((index) => {
      const edge = edges[index]
      const dirKey = `${edge.source}|${edge.target}`
      if (!byDirection.has(dirKey)) {
        byDirection.set(dirKey, [])
      }
      byDirection.get(dirKey)!.push(index)
    })

    const directions = Array.from(byDirection.values())
    const isBidirectional = directions.length >= 2

    if (isBidirectional) {
      // Bidirectional: nudge each direction onto a distinct named handle on its
      // (already mirrored) source/target faces so the two directions don't share
      // the exact same port. We assign opposite ends of the face: the first
      // direction takes the "left/top" handle, the second the "right/bottom".
      directions.forEach((dirIndices, dirOrder) => {
        // Within a direction there may also be parallel duplicates.
        spreadAcrossHandles(
          edges,
          nodePositions,
          dirIndices,
          hadExplicitSource,
          hadExplicitTarget,
          dirOrder // bias so opposite directions prefer opposite handle slots
        )
      })
    } else {
      // Pure parallel group (all same direction): spread across handles.
      spreadAcrossHandles(
        edges,
        nodePositions,
        indices,
        hadExplicitSource,
        hadExplicitTarget,
        0
      )
    }
  })
}

/**
 * Assign distinct named handles to a set of edges that share faces, spreading
 * them across the 3 available perimeter ports per face. Edges that end up with
 * a distinct source or target handle are flagged `portSeparated`.
 */
function spreadAcrossHandles(
  edges: DiagramEdge[],
  nodePositions: Map<string, NodePosition>,
  indices: number[],
  hadExplicitSource: boolean[],
  hadExplicitTarget: boolean[],
  slotBias: number
): void {
  if (indices.length === 0) {
    return
  }

  // Bucket by source face and target face so we only spread edges that would
  // otherwise share the same perimeter point.
  const sourceFaceBuckets = bucketByFace(edges, nodePositions, indices, 'source')
  const targetFaceBuckets = bucketByFace(edges, nodePositions, indices, 'target')

  applyFaceSpread(edges, sourceFaceBuckets, 'source', hadExplicitSource, slotBias)
  applyFaceSpread(edges, targetFaceBuckets, 'target', hadExplicitTarget, slotBias)
}

function bucketByFace(
  edges: DiagramEdge[],
  nodePositions: Map<string, NodePosition>,
  indices: number[],
  role: Role
): Map<HandleFace, number[]> {
  const buckets = new Map<HandleFace, number[]>()
  indices.forEach((index) => {
    const edge = edges[index]
    const face = role === 'target'
      ? determineTargetFace(edge, nodePositions)
      : determineSourceFace(edge, nodePositions)
    if (!face) {
      return
    }
    if (!buckets.has(face)) {
      buckets.set(face, [])
    }
    buckets.get(face)!.push(index)
  })
  return buckets
}

function applyFaceSpread(
  edges: DiagramEdge[],
  buckets: Map<HandleFace, number[]>,
  role: Role,
  hadExplicit: boolean[],
  slotBias: number
): void {
  buckets.forEach((bucketIndices, face) => {
    if (bucketIndices.length < 2 && slotBias === 0) {
      // A single edge entering/leaving a face doesn't need a distinct port
      // unless we're deliberately biasing opposite directions apart.
      return
    }

    const options = getHandleOptions(face, role)
    if (options.length === 0) {
      return
    }

    // Reserve the option slots already consumed by explicit edges in this
    // bucket so auto edges are never assigned the SAME perimeter port. Without
    // this, an explicit center handle plus an auto sibling could resolve to the
    // identical handle, fully overlapping the two edges with no fallback offset.
    const consumed = new Set<number>()
    bucketIndices.forEach((index) => {
      if (!hadExplicit[index]) {
        return
      }
      const explicitHandle = role === 'target'
        ? edges[index].targetHandle
        : edges[index].sourceHandle
      const explicitSlot = explicitHandle ? options.indexOf(explicitHandle) : -1
      if (explicitSlot >= 0) {
        consumed.add(explicitSlot)
      }
    })

    // Walk slots from the bias-derived starting point, stepping over any slot an
    // explicit edge already owns. Returns -1 when every port is taken (more auto
    // edges than ports), in which case we leave the edge un-flagged so the
    // distance-scaled offset pass can still nudge it apart (non-regressive).
    let cursor = slotBias
    const nextFreeSlot = (): number => {
      for (let attempt = 0; attempt < options.length; attempt++) {
        const slot = ((cursor % options.length) + options.length) % options.length
        cursor++
        if (!consumed.has(slot)) {
          consumed.add(slot)
          return slot
        }
      }
      return -1
    }

    bucketIndices.forEach((index) => {
      if (hadExplicit[index]) {
        // Never override an explicit handle.
        return
      }

      const slot = nextFreeSlot()
      const exhausted = slot < 0
      const handleId = options[exhausted ? slotBias % options.length : slot]

      if (role === 'target') {
        edges[index].targetHandle = handleId
      } else {
        edges[index].sourceHandle = handleId
      }

      // Only flag portSeparated when the edge actually got a port distinct from
      // every sibling. If we ran out of free ports it may share one, so leave the
      // flag off and let the distance-scaled offset pass separate it instead.
      if (!exhausted) {
        edges[index].data = { ...edges[index].data, portSeparated: true }
      }
    })
  })
}

function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`
}
